<template>
    <div class="counter-stop">
        <counter-header
            :stop-time="formattedTime"
            :stop-name="options.stopName"
            :booking-count="options.bookingCount"
            :edit-mode="editMode"
            :toggle-edit-mode="toggleEditMode"
            :editable="editable && !readOnly && view !== 'compact'"
            :no-show="currentNoShowBooking?.bookingNoShowType"
            @no-passengers="emit('no-passengers')"
            @delete-no-show="emit('delete-no-show', currentNoShowBooking)"
        />
        <v-container>
            <v-tabs v-model="tab" fixed-tabs>
                <v-tab value="in" data-id="counter-in-tab"> Inn </v-tab>
                <v-tab value="out" data-id="counter-out-tab"> Ut </v-tab>
            </v-tabs>
            <v-window v-model="tab">
                <v-window-item value="in">
                    <div class="counters-wrap">
                        <compact-counter-cell
                            v-for="(eventData, key) in options.stopEventData"
                            :key="key"
                            :count="eventData.in"
                            :edit-mode="false"
                            :disabled="disableAdd || readOnly"
                            :rider-type="key"
                            @input="updateInValue(key, $event)"
                        />
                    </div>
                </v-window-item>
                <v-window-item value="out">
                    <div class="counters-wrap">
                        <compact-counter-cell
                            v-for="(eventData, key) in options.stopEventData"
                            :key="key"
                            :count="eventData.out"
                            :edit-mode="false"
                            :disabled="disableRemove || readOnly"
                            :rider-type="key"
                            @input="updateOutValue(key, $event)"
                        />
                    </div>
                </v-window-item>
            </v-window>
            <v-row v-if="hasCarsLeftBehind" class="px-3">
                <v-col cols="12" class="pa-0 my-4">
                    <compact-counter-cell
                        :count="options.carsLeftBehind"
                        :edit-mode="false"
                        suffix="Kjøretøy akterutseilt"
                        rider-type="carsLeftBehind"
                        class="cars-behind"
                        :disabled="disableAdd || readOnly"
                        @input="updateCarsLeftBehind($event)"
                    />
                </v-col>
            </v-row>
            <v-row class="px-md-4">
                <counter-sum prefix="Totalt ombord" :sums="sums" />
            </v-row>
        </v-container>
    </div>
</template>

<script lang="ts" setup>
import CounterHeader from './CounterHeader.vue';
import CompactCounterCell from './CompactCounterCell.vue';
import CounterSum from './CounterSum.vue';
import { EntryMode } from '@/features/shared/models/EntryMode';
import { RiderTypes } from '@/features/shared/models/RiderType';
import { ref, computed, watch } from 'vue';
import { PassingTime } from '../PassingTime';
import { StopTotalsWithSum } from '../Stop';
import { BookingNoShowResponseDto } from '@/features/trips/BookingNoShowModels';

const props = defineProps<{
    options?: PassingTime;
    totals: StopTotalsWithSum;
    disableAdd: boolean;
    disableRemove: boolean;
    entryMode: EntryMode;
    editable: boolean;
    readOnly: boolean;
    view: string;
    hasNextStop: boolean;
    noShowBooking: Array<BookingNoShowResponseDto>;
}>();

const emit = defineEmits(['update', 'no-passengers', 'delete-no-show']);

const tab = ref('in');
const editMode = ref(props.entryMode === EntryMode.MANUAL);
const passangersIn = props.options.stopEventData?.passengers?.in ?? 0;
if (passangersIn > 0 && props.options.sequenceOrder === 1) {
    setInValue('passengers', passangersIn);
}

watch(
    () => props.hasNextStop,
    (val: boolean) => {
        tab.value = val ? 'in' : 'out';
    }
);

const formattedTime = computed(() => {
    const date = new Date(props.options.passingTime);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
});

const currentNoShowBooking = computed(() => {
    return props.noShowBooking.find(x => {
        return (
            x.tripStop.stop.quayRef === props.options.stop.quayRef &&
            x.tripStop.stop.publicCode === props.options.stop.publicCode &&
            x.tripStop.sequenceOrder === props.options.sequenceOrder
        );
    });
});

function toggleEditMode() {
    editMode.value = !editMode.value;
}

const sums = computed(() => {
    const sum = [{ count: props.totals.passengers, type: 'person' }];

    if (typeof props.totals.allCars === 'number') {
        sum.push({ count: props.totals.allCars, type: 'vehicle' });
    }

    return sum;
});

const hasCarsLeftBehind = computed(() => {
    return props.options.stopEventData && Object.prototype.hasOwnProperty.call(props.options.stopEventData, RiderTypes.Cars);
});

function setInValue(key: string, value: number) {
    const model = { ...props.options };
    const data = model.stopEventData[key];
    data.in = value;
    return model;
}

function setOutValue(key: string, value: number) {
    const model = { ...props.options };
    const data = model.stopEventData[key];
    data.out = value;
    return model;
}

function updateInValue(key: string, value: number) {
    const model = setInValue(key, value);
    emit('update', model);
}

function updateOutValue(key: string, value: number) {
    const model = setOutValue(key, value);
    emit('update', model);
}

function updateCarsLeftBehind(value: number) {
    const model = {
        ...props.options,
        carsLeftBehind: value
    };

    emit('update', model);
}
</script>

<style lang="scss" scoped>
.counters-wrap {
    padding-top: 16px;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
}

.counter-btn-cells {
    .v-icon--size-default {
        font-size: calc(var(--v-icon-size-multiplier) * 1.5em);
    }
}
</style>
@/features/shared/models/EntryMode@/features/shared/models/RiderType
